<script setup lang="ts">
import { useRecordPageContext } from '@/composables/recordPageContext';
import { computed, ref, watch } from 'vue';
import { refDebounced } from '@vueuse/core';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import InspectorTabsMenu from '@/components/DataTable/InspectorTabsMenu.vue';
import LzpIssueCommandSection from '@/components/DataTable/LzpIssueCommandSection.vue';
import LzpPendingCommandsSection from '@/components/DataTable/LzpPendingCommandsSection.vue';
import LzpLogsSection from '@/components/DataTable/LzpLogsSection.vue';
import LzpCommandHistorySection from '@/components/DataTable/LzpCommandHistorySection.vue';
import LzpScheduleSection from '@/components/DataTable/LzpScheduleSection.vue';
import SearchResultFieldSections from '@/components/DataTable/SearchResultFieldSections.vue';
import LzpCurrentPlaylistValue from '@/components/DataTable/LzpCurrentPlaylistValue.vue';
import LzpCurrentTrackValue from '@/components/DataTable/LzpCurrentTrackValue.vue';
import { markRaw } from 'vue';
import { groupColumnsForInspector } from '@/composables/searchInspectorFieldGroups';
import { useModelRecordQuery } from '@/composables/api/useModelRecordQuery';
import { liveRecordPollMsFor } from '@/composables/searchInspectorRegistry';
import {
    useLocationZonePlayerCommands,
    useLocationZonePlayerLogs,
    type PlayerLogRange,
} from '@/composables/api/useLocationZonePlayerInspectorApi';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * Inspector for a Location Zone Player — the single detail view used by the
 * cards split pane AND the side-peek overlay.
 *
 * One layout at every width: a [Details] / [Cmd & Logs] / [Command History] / [Schedule]
 * section switcher with the choice persisted. The field list sits behind "Details" rather than in a
 * permanent side column, so the split pane and the peek read the same. The
 * player's identity (status line, id, location/zone) is the HOST's title, so
 * this content never renders one of its own.
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /** Every column of the entity (visible or not) in table order. */
        columns: TableColumnStaticProps[];
        /** Column ids the header already shows. */
        excludeFields?: string[];
        /** Accepted for prop-shape parity with SearchResultInspector (always this entity). */
        entityId?: string;
    }>(),
    { excludeFields: () => [], entityId: 'LocationZonePlayer' }
);

const ENTITY_ID = 'LocationZonePlayer';

/**
 * Two check-in fields are worth more than their stored text. The playlist is
 * reported as a filename, so it renders as its real name and opens the record.
 * The track is reported as a code, so it renders as the compact track card.
 */
const FIELD_OVERRIDES = {
    player_data_current_playlist_name: { component: markRaw(LzpCurrentPlaylistValue) },
    player_data_current_track_code: { component: markRaw(LzpCurrentTrackValue), fullWidth: true },
};

// Persisted section choice (pattern: AudioFileIssueInspector).
const KNOWN_TABS = ['details', 'activity', 'command-history', 'schedule'] as const;
type InspectorTabValue = (typeof KNOWN_TABS)[number];
const TAB_DEFAULT: InspectorTabValue = 'details';
const TAB_STORAGE_KEY = 'location-zone-player-inspector:tab:v1';

function isKnownTab(value: unknown): value is InspectorTabValue {
    return typeof value === 'string' && (KNOWN_TABS as readonly string[]).includes(value);
}

function readPersistedTab(): InspectorTabValue {
    if (typeof window === 'undefined') {
        return TAB_DEFAULT;
    }
    try {
        const stored = window.localStorage.getItem(TAB_STORAGE_KEY);
        if (isKnownTab(stored)) {
            return stored;
        }
    } catch {
        // SecurityError / quota / malformed storage all fall back to the default.
    }
    return TAB_DEFAULT;
}

const inspectorTab = ref<InspectorTabValue>(readPersistedTab());

watch(inspectorTab, (nextTab) => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(TAB_STORAGE_KEY, nextTab);
    } catch {
        // Quota / private-mode writes shouldn't break the tab switch.
    }
});

function onTabChange(value: unknown) {
    if (isKnownTab(value)) {
        inspectorTab.value = value;
    }
}

// Wherever RecordBody draws a record (its page, the side peek, the split pane)
// the person's chosen layout decides the groups. Anywhere else the built-in
// grouping stays.
const recordPage = useRecordPageContext();
const groups = computed(
    () =>
        recordPage?.groupsFor(props.columns, props.excludeFields) ??
        groupColumnsForInspector(ENTITY_ID, props.columns, props.excludeFields)
);
const locationZonePlayerId = computed(() => {
    const id = props.row.id;
    return typeof id === 'number' ? id : id ? Number(id) : null;
});

// Poll the player's own row while the inspector is open so live check-in state
// (now-playing, volume, online status, time-since-check-in) refreshes — the
// commands/logs cards already poll, this brings the player data with them. The
// query key is shared with the overlay header and the id-cell record fetch, so
// it's a single request; fresh values override the (possibly stale) row prop.
const liveRecordQuery = useModelRecordQuery(ENTITY_ID, locationZonePlayerId, {
    refetchIntervalMs: liveRecordPollMsFor(ENTITY_ID),
});
const effectiveRow = computed<Record<string, unknown>>(() => ({
    ...props.row,
    ...(liveRecordQuery.data.value ?? {}),
}));

const commandsQuery = useLocationZonePlayerCommands(locationZonePlayerId);

// Server-side log filters; text inputs debounce before hitting the API.
const logRange = ref<PlayerLogRange>('all');
const logMessageFilter = ref('');
const logMessageMode = ref<'contains' | 'not_contains'>('contains');
const logTrackFilter = ref('');
const logTypes = ref<string[]>([]);
const logTypesMode = ref<'in' | 'not_in'>('in');
const logMessageDebounced = refDebounced(logMessageFilter, 350);
const logTrackDebounced = refDebounced(logTrackFilter, 350);

// Trimmed here so the query key matches the (trimmed) request params —
// otherwise "foo" and "foo " would be distinct cache entries for one request.
const logsQuery = useLocationZonePlayerLogs(locationZonePlayerId, () => ({
    range: logRange.value,
    message: logMessageDebounced.value.trim(),
    messageMode: logMessageMode.value,
    track: logTrackDebounced.value.trim(),
    types: logTypes.value,
    typesMode: logTypesMode.value,
}));

const waiting = computed(() => commandsQuery.data.value?.waiting ?? []);
const queued = computed(() => commandsQuery.data.value?.queued ?? []);
const pendingCount = computed(() => waiting.value.length + queued.value.length);
const logs = computed(() => logsQuery.data.value?.data ?? []);
const logsMeta = computed(() => logsQuery.data.value?.meta ?? null);
</script>

<template>
    <!-- min-h-full (not h-full): inside the overflow-auto hosts this resolves to
         max(content, 100%), so the flex-1 chain below lets the Logs card absorb
         leftover vertical space while long content still scrolls the host.
         flex-1 covers the cards split pane, whose slot wrapper is a flex column. -->
    <div class="flex min-h-full flex-1 flex-col gap-4" data-testid="location-zone-player-inspector">
        <!-- One layout at every width: the [Details] / [Cmd & Logs] / [Command History] / [Schedule] switcher
             (persisted). The fields live behind "Details" rather than in a permanent
             side column, so the inspector reads the same in the split pane and the peek. -->
        <Tabs
            :model-value="inspectorTab"
            class="flex min-h-0 flex-1 flex-col gap-4"
            data-testid="lzp-compact-tabs"
            @update:model-value="onTabChange"
        >
            <InspectorTabsMenu
                :model-value="inspectorTab"
                :items="[
                    { value: 'details', label: 'Details' },
                    { value: 'activity', label: 'Cmd & Logs', badge: pendingCount },
                    { value: 'command-history', label: 'Command History' },
                    { value: 'schedule', label: 'Schedule' },
                ]"
                @update:model-value="onTabChange"
            />
            <TabsContent value="details" class="mt-0 outline-none">
                <SearchResultFieldSections
                    :row="effectiveRow"
                    :groups="groups"
                    :entity-id="ENTITY_ID"
                    :field-overrides="FIELD_OVERRIDES"
                />
            </TabsContent>
            <TabsContent value="activity" class="mt-0 flex min-h-0 flex-col gap-4 outline-none">
                <LzpIssueCommandSection class="flex-none" :player="effectiveRow" />
                <LzpPendingCommandsSection
                    class="flex-none"
                    :waiting="waiting"
                    :queued="queued"
                    :is-loading="commandsQuery.isLoading.value"
                    :is-error="commandsQuery.isError.value"
                    :location-zone-player-id="locationZonePlayerId"
                />
                <LzpLogsSection
                    v-model:range="logRange"
                    v-model:message-filter="logMessageFilter"
                    v-model:track-filter="logTrackFilter"
                    v-model:message-mode="logMessageMode"
                    v-model:types="logTypes"
                    v-model:types-mode="logTypesMode"
                    :logs="logs"
                    :meta="logsMeta"
                    :is-loading="logsQuery.isLoading.value"
                    :is-error="logsQuery.isError.value"
                    :location-zone-player-id="locationZonePlayerId"
                />
            </TabsContent>
            <!-- TabsContent unmounts while hidden, so the history only loads while this tab shows. -->
            <TabsContent value="command-history" class="mt-0 outline-none">
                <LzpCommandHistorySection :location-zone-player-id="locationZonePlayerId" />
            </TabsContent>
            <TabsContent value="schedule" class="mt-0 outline-none">
                <LzpScheduleSection
                    v-if="inspectorTab === 'schedule'"
                    :location-zone-player-id="locationZonePlayerId"
                    :player="effectiveRow"
                />
            </TabsContent>
        </Tabs>
    </div>
</template>
